import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, CircularProgress } from "@mui/material";
import { Bar } from 'react-chartjs-2';
import { makeStyles } from "@material-ui/core/styles";
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip } from "chart.js";
import { getReplacementCarRequests } from "../../store/action/claimManagement";
import Skeleton from "@mui/material/Skeleton";
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const useStyles = makeStyles((theme) => ({
    chartContainer: {
        //margin: theme.spacing(1),
        backgroundColor: 'rgba(255, 255, 255, 1)',
        borderRadius: '9px',
        padding: theme.spacing(2),
        width: '49.5%',
        boxSizing: 'border-box',
    },
    title: {
        marginLeft: theme.spacing(2),
        fontFamily: 'Poppins',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '22px',
        letterSpacing: '0.5px',
        textAlign: 'left',
        marginBottom: theme.spacing(2),
        color: 'rgba(0, 0, 0, 1)'
    },
    subTitle1: {
        marginLeft: theme.spacing(2),
        marginBottom: theme.spacing(1),
        fontFamily: 'Poppins',
        fontSize: '20px',
        fontWeight: 500,
        lineHeight: '28px',
        letterSpacing: '0.5px',
        textAlign: 'left',
    },
    subTitle2: {
        marginLeft: theme.spacing(2),
        marginBottom: theme.spacing(2),
        fontFamily: 'Poppins',
        fontSize: '13px',
        fontWeight: 400,
        lineHeight: '21px',
        letterSpacing: '0.5px',
        textAlign: 'left',
        color: 'rgb(127, 127, 127)',
    },
    filterContainer: {
        display: 'flex',
        marginBottom: theme.spacing(2),
    },
    legendItem: {
        margin: theme.spacing(0.5, 1),
        display: 'flex',
        alignItems: 'center',
    },
    legendColorBox: {
        borderRadius: '50%',
        width: 15,
        height: 15,
        marginRight: theme.spacing(0.5),
    },
    legendLabel: {
        fontSize: '12px',
        fontWeight: 400,
        fontFamily: 'Poppins',
        color: 'rgb(90, 90, 90)'
    },
    barChartLegend: {
        display: 'flex',
        marginTop: theme.spacing(2),
        // justifyContent: 'center',
        flexWrap: 'wrap',
    }
}));

const MonthlyReplacementCarRequest = () => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const { replacementCarRequestsData, loading } = useSelector((state) => state.replacementCarRequestsReducer);
    // console.log("replacementCarRequestsData", replacementCarRequestsData)
    // console.log("loading", loading)

    useEffect(() => {
        dispatch(getReplacementCarRequests());
    }, [dispatch]);

    if (loading) {
        return <Skeleton sx={{ height: 300 }} animation="wave" variant="rectangular"/>;
    }

    // if (!replacementCarRequestsData || replacementCarRequestsData.length === 0) {
    //     return <Typography>No data available</Typography>;
    // }

    const calculateAnnualTotal = (requests) => {
        const currentYear = new Date().getFullYear();
        return requests.filter(req => new Date(req.createdAt).getFullYear() === currentYear).length;
    };

    const currentMonth = new Date().getMonth() + 1; // cuz months are 0-indexed
    const today = new Date().getDate();
    const currentYear = new Date().getFullYear();

    // grp data by months for the current year
    const groupByMonth = (requests) => {
        let monthData = Array.from({ length: 12 }, () => ({ approved: 0, rejected: 0, grantedDays: 0 }));
        requests.forEach(req => {
            const requestDate = new Date(req.replacementCarApprovalDate);
            if (requestDate.getFullYear() === currentYear) {
                const month = requestDate.getMonth();
                if (req.replacementStatus === "APPROVED") {
                    monthData[month].approved++;
                    monthData[month].grantedDays += req.numberOfGrantedDays || 0;
                } else if (req.replacementStatus === "REJECTED") {
                    monthData[month].rejected++;
                }
            }
        });

        return monthData;
    };

    const monthData = groupByMonth(replacementCarRequestsData);

    const barData = {
        //labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        labels: Array.from({ length: 12 }, (_, i) => (i + 1).toString()),
        datasets: [
            {
                label: 'Demandes acceptés',
                data: monthData.map(month => month.approved),
                backgroundColor: 'rgba(60, 101, 86, 1)',
            },
            {
                label: 'Demandes refusées',
                data: monthData.map(month => month.rejected),
                backgroundColor: 'rgba(240, 243, 199, 1)',
            },
            {
                label: 'Nombre de jours accordés',
                data: monthData.map(month => month.grantedDays),
                backgroundColor: 'rgba(150, 172, 143, 1)',
            }
        ]
    };

    const managementTypeData = {
        "Demandes acceptées": monthData.reduce((sum, month) => sum + month.approved, 0),
        "Demandes refusées": monthData.reduce((sum, month) => sum + month.rejected, 0),
        "Nombre de jours accordés": monthData.reduce((sum, month) => sum + month.grantedDays, 0),
    };

    return (
        <Box className={classes.chartContainer}>
            <Typography className={classes.title}>Suivi demande de VR</Typography>
            <Typography className={classes.subTitle1}>Total Annuel {calculateAnnualTotal(replacementCarRequestsData)}</Typography>
            <Typography className={classes.subTitle2}>
                Demande de VR du 01/{currentMonth < 10 ? `0${currentMonth}` : currentMonth} au {today}/{currentYear}
            </Typography>
            <Box height="100%">
                <Bar
                    data={barData}
                    options={{
                        responsive: true,
                        scales: {
                            x: {
                                beginAtZero: true,
                                grid: {
                                    display: false, // rmv grid
                                }
                            },
                            y: {
                                beginAtZero: true,
                                grid: {
                                    display: true,
                                    borderDash: [5, 15], // Dotted line
                                    color: 'rgba(226, 231, 231, 1)',
                                    lineWidth: 1,
                                    drawTicks: true,
                                },
                                ticks: {
                                    display: false,
                                },
                                max: Math.max(...monthData.map(month => Math.max(month.approved, month.rejected))) + 1,
                            },
                        },
                        plugins: {
                            legend: {
                                display: false
                            }
                        },
                        // barThickness: 15,
                        // maxBarThickness: 15,
                        barPercentage: 0.6, // bar width
                        categoryPercentage: 0.5, // category width
                        grouped: {
                            barSpacing: 3,
                        }
                    }}
                />
            </Box>
            <Box className={classes.barChartLegend}>
                {Object.keys(managementTypeData).map((label, index) => (
                    <Box key={label} className={classes.legendItem}>
                        <Box
                            className={classes.legendColorBox}
                            style={{ backgroundColor: barData.datasets[index].backgroundColor }}
                        />
                        <Typography className={classes.legendLabel}>
                            {label}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default MonthlyReplacementCarRequest;
